import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Layers,
  Database,
  Filter,
  Microscope,
  Quote,
  ExternalLink,
  AlertTriangle,
  CheckCircle2,
  MinusCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";

// ─────────────────────────────────────────────────────────────────────────────
// Enrichment tab = the L0->L3 enrichment ladder for THIS account.
//   L0  name only
//   L1  name + domain
//   L2  Clay firmographics landed (with per-field provenance)
//   L3  custom deep-scrape, every non-obvious claim carrying a verbatim quote
//
// Canonical doctrine: launch-platform/template/brain/ACCOUNT_ENRICHMENT_LEVELS.md
// Provenance shape:   launch-platform/template/brain/ENRICHMENT_PROVENANCE_CONTRACT.md
// Runbook:            docs/runbooks/account_enrichment_ladder.md
//
// This tab is deliberately read-only. It exists so a human can answer "where did
// this value come from, and should I trust it?" without opening the database.
// ─────────────────────────────────────────────────────────────────────────────

interface AccountEnrichmentTabProps {
  accountId: string;
}

interface ProvenanceEntry {
  source?: string;
  enriched_at?: string;
  method?: string;
  evidence?: string;
  source_url?: string;
  quote?: string;
  note?: string;
}

interface EnrichmentRow {
  id: string;
  company_name: string | null;
  domain: string | null;
  website: string | null;
  field_provenance: Record<string, ProvenanceEntry> | null;
  clay_enrichment_status: string | null;
  clay_enriched_at: string | null;
  clay_provenance: string | null;
  clay_source_data: Record<string, unknown> | null;
  industry_segment: string | null;
  l3_eligible: boolean | null;
  carr_enrichment_decision: string | null;
  carr_enrichment_engine: string | null;
  carr_enrichment_decision_reason: string | null;
  carr_enrichment_decision_breakdown: Record<string, unknown> | null;
  carr_enrichment_decision_version: string | null;
  carr_enrichment_decision_scored_at: string | null;
  account_intel: Record<string, unknown> | null;
  scrape_status: string | null;
  scraped_at: string | null;
}

const SELECT_COLS = [
  "id",
  "company_name",
  "domain",
  "website",
  "field_provenance",
  "clay_enrichment_status",
  "clay_enriched_at",
  "clay_provenance",
  "clay_source_data",
  "industry_segment",
  "l3_eligible",
  "carr_enrichment_decision",
  "carr_enrichment_engine",
  "carr_enrichment_decision_reason",
  "carr_enrichment_decision_breakdown",
  "carr_enrichment_decision_version",
  "carr_enrichment_decision_scored_at",
  "account_intel",
  "scrape_status",
  "scraped_at",
].join(",");

// Mirrors public.account_enrichment_level() so UX and SQL never disagree.
function deriveLevel(row: EnrichmentRow): 0 | 1 | 2 | 3 {
  if (row.scrape_status === "scraped" && row.account_intel) return 3;
  if (row.clay_enrichment_status === "enriched") return 2;
  const host = (row.domain || "").trim() || (row.website || "").trim();
  if (host) return 1;
  return 0;
}

const LEVEL_COPY: Record<number, { label: string; detail: string }> = {
  0: { label: "L0 — name only", detail: "No domain yet, so nothing downstream can run." },
  1: { label: "L1 — name + domain", detail: "Identified. Ready to send to Clay for firmographics." },
  2: { label: "L2 — Clay firmographics", detail: "Firmographics landed. The filter decides if a deep scrape is worth it." },
  3: { label: "L3 — deep scrape", detail: "Custom intel captured from the account's own pages, with quotes." },
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function humanize(key: string): string {
  return key.replace(/_/g, " ");
}

function renderValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) return value.length ? value.map(v => String(v)).join(", ") : "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function LadderRail({ level }: { level: number }) {
  return (
    <div className="flex items-center gap-1">
      {[0, 1, 2, 3].map(step => (
        <div key={step} className="flex items-center gap-1">
          <div
            className={`h-7 w-7 rounded-full grid place-items-center text-xs font-semibold ${
              step <= level ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
            }`}
            title={LEVEL_COPY[step]?.label}
          >
            {`L${step}`}
          </div>
          {step < 3 && (
            <div className={`h-0.5 w-6 ${step < level ? "bg-primary" : "bg-muted"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

function SectionHeader({
  Icon,
  title,
  subtitle,
}: {
  Icon: typeof Layers;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="flex items-start gap-2 mb-3">
      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
      <div>
        <h3 className="text-sm font-semibold">{title}</h3>
        {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
      </div>
    </div>
  );
}

function DecisionBadge({ decision }: { decision: string | null }) {
  if (!decision) return <Badge variant="outline">not scored</Badge>;
  if (decision === "enrich") {
    return (
      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">
        <CheckCircle2 className="h-3 w-3 mr-1" />
        enrich
      </Badge>
    );
  }
  if (decision === "skip") {
    return (
      <Badge variant="secondary">
        <MinusCircle className="h-3 w-3 mr-1" />
        skip
      </Badge>
    );
  }
  return (
    <Badge className="bg-amber-100 text-amber-900 hover:bg-amber-100">
      <AlertTriangle className="h-3 w-3 mr-1" />
      insufficient data
    </Badge>
  );
}

export function AccountEnrichmentTab({ accountId }: AccountEnrichmentTabProps) {
  const { data: row, isLoading, error } = useQuery({
    queryKey: ["account-enrichment", accountId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("companies")
        .select(SELECT_COLS)
        .eq("id", accountId)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as EnrichmentRow) ?? null;
    },
  });

  const provenanceRows = useMemo(() => {
    const prov = row?.field_provenance;
    if (!prov || typeof prov !== "object") return [];
    return Object.entries(prov)
      .map(([field, entry]) => ({ field, entry: (entry || {}) as ProvenanceEntry }))
      .sort((a, b) => a.field.localeCompare(b.field));
  }, [row]);

  const intelRows = useMemo(() => {
    const intel = row?.account_intel;
    if (!intel || typeof intel !== "object") return [];
    return Object.entries(intel).sort((a, b) => a[0].localeCompare(b[0]));
  }, [row]);

  if (isLoading) {
    return <div className="p-6 text-sm text-muted-foreground">Loading enrichment state…</div>;
  }

  if (error) {
    return (
      <div className="p-6 text-sm text-destructive">
        Could not load enrichment state: {(error as Error).message}
      </div>
    );
  }

  if (!row) {
    return <div className="p-6 text-sm text-muted-foreground">No account row found.</div>;
  }

  const level = deriveLevel(row);
  const columnOnly = row.clay_provenance === "column_only";

  return (
    <div className="p-6 space-y-8">
      {/* ── Ladder position ─────────────────────────────────────────────── */}
      <div>
        <SectionHeader
          Icon={Layers}
          title="Enrichment level"
          subtitle="How far up the L0-L3 ladder this account has been carried."
        />
        <div className="flex flex-wrap items-center gap-4">
          <LadderRail level={level} />
          <div>
            <div className="text-sm font-medium">{LEVEL_COPY[level].label}</div>
            <div className="text-xs text-muted-foreground">{LEVEL_COPY[level].detail}</div>
          </div>
        </div>
      </div>

      {/* ── L2: Clay ────────────────────────────────────────────────────── */}
      <div>
        <SectionHeader
          Icon={Database}
          title="L2 — Clay firmographics"
          subtitle="Synced from the Hub. Firmographics only; Clay cannot see how an account manufactures."
        />
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Status</dt>
            <dd className="font-medium">{row.clay_enrichment_status || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Enriched</dt>
            <dd className="font-medium">{fmtDate(row.clay_enriched_at)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Attribution</dt>
            <dd className="font-medium">{row.clay_provenance || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Industry segment</dt>
            <dd className="font-medium">{row.industry_segment || "—"}</dd>
          </div>
        </dl>

        {columnOnly && (
          <p className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            <AlertTriangle className="h-3 w-3 inline mr-1" />
            Attribution is <strong>column_only</strong>: the enrichment happened upstream but
            per-field provenance was never recorded. Treat these values as weaker than
            field-attributed Clay data.
          </p>
        )}

        {provenanceRows.length > 0 && (
          <div className="mt-4 border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">Field</th>
                  <th className="px-3 py-2 font-medium">Source</th>
                  <th className="px-3 py-2 font-medium">Method</th>
                  <th className="px-3 py-2 font-medium">When</th>
                  <th className="px-3 py-2 font-medium">Evidence</th>
                </tr>
              </thead>
              <tbody>
                {provenanceRows.map(({ field, entry }) => {
                  const evidence = entry.evidence || entry.quote || entry.note || "";
                  return (
                    <tr key={field} className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">{field}</td>
                      <td className="px-3 py-2">{entry.source || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{entry.method || "—"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{fmtDate(entry.enriched_at)}</td>
                      <td className="px-3 py-2 text-muted-foreground max-w-md">
                        {evidence ? (
                          <span className="italic">
                            <Quote className="h-3 w-3 inline mr-1" />
                            {evidence}
                          </span>
                        ) : (
                          "—"
                        )}
                        {entry.source_url && (
                          <a
                            href={entry.source_url}
                            target="_blank"
                            rel="noreferrer"
                            className="ml-2 text-primary inline-flex items-center"
                          >
                            source
                            <ExternalLink className="h-3 w-3 ml-0.5" />
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {provenanceRows.length === 0 && (
          <p className="mt-3 text-xs text-muted-foreground">
            No per-field provenance recorded for this account yet.
          </p>
        )}
      </div>

      {/* ── Filter verdict ──────────────────────────────────────────────── */}
      <div>
        <SectionHeader
          Icon={Filter}
          title="L2 → L3 filter"
          subtitle="Is a deep scrape worth spending on? Deterministic rules first, LLM only for the gray band."
        />
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <div>
            <dt className="text-xs text-muted-foreground">Verdict</dt>
            <dd className="font-medium mt-1">
              <DecisionBadge decision={row.carr_enrichment_decision} />
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Engine</dt>
            <dd className="font-medium">{row.carr_enrichment_engine || "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Config version</dt>
            <dd className="font-medium font-mono text-xs">
              {row.carr_enrichment_decision_version || "—"}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Scored</dt>
            <dd className="font-medium">{fmtDate(row.carr_enrichment_decision_scored_at)}</dd>
          </div>
        </dl>

        {row.carr_enrichment_decision_reason && (
          <p className="mt-3 text-sm text-muted-foreground">
            {row.carr_enrichment_decision_reason}
          </p>
        )}

        {row.carr_enrichment_decision_breakdown && (
          <details className="mt-3">
            <summary className="text-xs text-primary cursor-pointer">Score breakdown</summary>
            <pre className="mt-2 text-xs bg-muted/50 rounded p-3 overflow-x-auto">
              {JSON.stringify(row.carr_enrichment_decision_breakdown, null, 2)}
            </pre>
          </details>
        )}
      </div>

      {/* ── L3 intel ────────────────────────────────────────────────────── */}
      <div>
        <SectionHeader
          Icon={Microscope}
          title="L3 — deep scrape intel"
          subtitle="What Clay can never return: how this account separates cells, and who they buy it from."
        />
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm mb-4">
          <div>
            <dt className="text-xs text-muted-foreground">Scrape status</dt>
            <dd className="font-medium">{row.scrape_status || "never attempted"}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Scraped</dt>
            <dd className="font-medium">{fmtDate(row.scraped_at)}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">L3 eligible</dt>
            <dd className="font-medium">
              {row.l3_eligible === null ? "—" : row.l3_eligible ? "yes" : "no"}
            </dd>
          </div>
        </dl>

        {intelRows.length > 0 ? (
          <div className="border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left">
                  <th className="px-3 py-2 font-medium">Field</th>
                  <th className="px-3 py-2 font-medium">Value</th>
                  <th className="px-3 py-2 font-medium">Evidence</th>
                </tr>
              </thead>
              <tbody>
                {intelRows.map(([field, value]) => {
                  const prov = row.field_provenance?.[`account_intel.${field}`];
                  const evidence = prov?.evidence || prov?.quote || "";
                  return (
                    <tr key={field} className="border-t align-top">
                      <td className="px-3 py-2 capitalize whitespace-nowrap">{humanize(field)}</td>
                      <td className="px-3 py-2">{renderValue(value)}</td>
                      <td className="px-3 py-2 text-muted-foreground max-w-md">
                        {evidence ? (
                          <span className="italic">
                            <Quote className="h-3 w-3 inline mr-1" />
                            {evidence}
                          </span>
                        ) : (
                          "—"
                        )}
                        {prov?.source_url && (
                          <a
                            href={prov.source_url}
                            target="_blank"
                            rel="noreferrer"
                            className="ml-2 text-primary inline-flex items-center"
                          >
                            source
                            <ExternalLink className="h-3 w-3 ml-0.5" />
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">
            No L3 intel yet.{" "}
            {row.l3_eligible
              ? "This account passed the filter and is queued for a deep scrape."
              : "The filter has not cleared this account for a deep scrape."}
          </p>
        )}
      </div>
    </div>
  );
}
